// Agent chats on the screen: the list, one chat, its entries (a gapless line
// by seq), its turn list, runs and requests, this person's devices, the reply
// being written (live), and every write.
//
// Promise:
// - The entries of a chat are one contiguous run of seqs. The first read takes
//   the newest page; later reads (after an event) take what comes after the
//   newest entry, plus everything from the oldest entry an event said changed
//   (a reply finished, a post taken back), and merge it in. Older pages are
//   put in front on demand. No seq is ever skipped.
// - The words of a reply being written and the progress of a run arrive live
//   (not stored) and are kept per chat until the stored entry replaces them.
// - A post shows in the render it is sent in, after the stored entries, until
//   the line has read up to it (`usePendingPosts`; not written into the cache).
//   Once the server answered it shows as sent, even when the line has not
//   read the entries before it yet, and never twice.
import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  type MutationCache,
  type Query,
  type QueryClient,
  type QueryKey,
  queryOptions,
  replaceEqualDeep,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AgentChatDTO,
  AgentChatListDTO,
  AgentChatRef,
  AgentDeviceDTO,
  AgentEntryDTO,
  AgentEntryFile,
  AgentOutline,
  AgentOutlineHead,
  AgentRequestDTO,
  AgentRequestKind,
  AgentRunDTO,
  AgentTurnItemDTO,
  AgentUndoResultDTO,
} from "@trameria/core/agent-chat-shared";
import type { CaseDetail } from "@trameria/core/cases-shared";
import type { Change, RunLive, TurnAnswer } from "@trameria/shared-agent";
import { DEVICE_SETTINGS_DEFAULT } from "@trameria/shared-agent";
import type { Signed } from "@trameria/shared-agent/instruction";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useApi } from "../app/context";
import { type Api, ApiError, idempotencyKeyFor } from "../data/api";
import { OPTIMISTIC_PREFIX } from "../data/optimistic";
import { badgeChanged, inboxRowsSettled } from "../notices/queries";
import {
  chatForSigning,
  conditionsOf,
  type InstructionSigner,
  SignRefused,
  signOne,
  useSigner,
} from "./signing";
import { fileHashOf } from "./uploads";

/** The id of a post on its way (an optimistic id: never linked to or fetched). */
const PENDING_POST = `${OPTIMISTIC_PREFIX}post:`;

import { type ColumnGap, putGapPage, putJumpPage } from "./column-gap";
import { unread } from "./list-rows";
import { shownDevice } from "./names";

// ---- keys ------------------------------------------------------------------------

export const agentsPrefix = ["me", "agents"] as const;
export const chatListsPrefix = [...agentsPrefix, "list"] as const;
export const chatsPrefix = [...agentsPrefix, "chat"] as const;
export const chatKey = (key: string) => [...chatsPrefix, key] as const;
export const entriesKey = (chatId: string) =>
  [...agentsPrefix, "entries", chatId] as const;
export const turnKey = (chatId: string) =>
  [...agentsPrefix, "turn", chatId] as const;
export const runsKey = (chatId: string) =>
  [...agentsPrefix, "runs", chatId] as const;
export const requestsKey = (chatId: string) =>
  [...agentsPrefix, "requests", chatId] as const;
export const childrenKey = (chatId: string) =>
  [...agentsPrefix, "children", chatId] as const;
export const sideKey = (chatId: string) =>
  [...agentsPrefix, "side", chatId] as const;
export const outlineKey = (chatId: string) =>
  [...agentsPrefix, "outline", chatId] as const;
export const undoKey = (chatId: string) =>
  [...agentsPrefix, "undo", chatId] as const;
export const changesKey = (chatId: string) =>
  [...agentsPrefix, "changes", chatId] as const;
export const devicesKey = [...agentsPrefix, "devices"] as const;
export const liveKey = (chatId: string) =>
  [...agentsPrefix, "live", chatId] as const;

const enc = encodeURIComponent;
const chatPath = (idOrKey: string) => `/api/me/agents/chats/${enc(idOrKey)}`;

// ---- reads -----------------------------------------------------------------------

export const chatListQuery = (
  api: Api,
  options: { workspace: string | null; archived?: boolean },
) =>
  queryOptions({
    queryKey: [
      ...chatListsPrefix,
      options.archived ? "archived" : "open",
      options.workspace ?? "",
    ] as const,
    queryFn: () => {
      const params = new URLSearchParams({ limit: "200" });
      if (options.archived) params.set("archived", "1");
      if (options.workspace !== null)
        params.set("workspace", options.workspace);
      return api<AgentChatListDTO>(`/api/me/agents/chats?${params}`);
    },
  });

/** A page of the Agents list (read on as it is scrolled, the old web's 50). */
const CHAT_PAGE = 50;
/** Where "open" / "active" / "archived" stands in the pages' key. */
const CHAT_PAGES_SIDE = chatListsPrefix.length + 1;

/**
 * The Agents list, page by page (the newest change first): open, only the
 * active ones (the server picks them, so a page is full of them) or archived,
 * the Workspace of Me, and the typed word (the server finds a title not read yet).
 */
export const chatPagesQuery = (
  api: Api,
  options: {
    workspace: string | null;
    archived?: boolean;
    active?: boolean;
    q?: string;
  },
) => {
  const side = options.archived
    ? "archived"
    : options.active
      ? "active"
      : "open";
  return infiniteQueryOptions({
    queryKey: [
      ...chatListsPrefix,
      "pages",
      side,
      options.workspace ?? "",
      options.q ?? "",
    ] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(CHAT_PAGE) });
      if (side === "archived") params.set("archived", "1");
      if (side === "active") params.set("active", "1");
      if (options.workspace !== null)
        params.set("workspace", options.workspace);
      if (options.q) params.set("q", options.q);
      if (pageParam) params.set("cursor", pageParam);
      return api<AgentChatListDTO>(`/api/me/agents/chats?${params}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // The rows stay while a typed word or a Workspace is asked again, but one
    // chip's chats do not stand in for another's while those load.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[CHAT_PAGES_SIDE] === side
        ? keepPreviousData(previous)
        : undefined,
  });
};

const belowPrefix = [...agentsPrefix, "below"] as const;

/**
 * Every chat under a top chat of the Agents list (all depths), under the
 * list's filter: read when its folded rows are opened, as the list's pages
 * bring a family's older children only as they are scrolled to. Not kept
 * fresh by the events; the list's own copy of a chat wins over this one.
 */
export const chatsBelowQuery = (
  api: Api,
  chatId: string,
  options: { workspace: string | null; archived: boolean },
) =>
  queryOptions({
    queryKey: [
      ...belowPrefix,
      chatId,
      options.archived ? "archived" : "open",
      options.workspace ?? "",
    ] as const,
    queryFn: async () => {
      const items: AgentChatDTO[] = [];
      let cursor: string | null = null;
      do {
        const params = new URLSearchParams({ below: chatId, limit: "200" });
        if (options.archived) params.set("archived", "1");
        if (options.workspace !== null)
          params.set("workspace", options.workspace);
        if (cursor) params.set("cursor", cursor);
        const page: AgentChatListDTO = await api<AgentChatListDTO>(
          `/api/me/agents/chats?${params}`,
        );
        items.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor);
      return items;
    },
  });

/** One chat by its key (the path) or id. */
export const chatQuery = (api: Api, key: string) =>
  queryOptions({
    queryKey: chatKey(key),
    queryFn: () => api<AgentChatDTO>(chatPath(key)),
  });

export interface EntriesData {
  items: AgentEntryDTO[];
  /** Entries before the first one exist on the server. */
  hasOlder: boolean;
  /**
   * The lowest seq an event said changed since the last read (a reply
   * finished, a post taken back); the next read starts there.
   */
  refreshFrom: number | null;
  /**
   * Entries not read between the head and what follows (after a jump to the
   * head: the pages between are read as the person comes down to them).
   * `after` is the last seq read above it, `before` the first below.
   */
  gap?: ColumnGap | null;
}

export const ENTRIES_PAGE = 50;
const ENTRIES_AFTER_MAX = 200;

type EntriesAnswer = { items: AgentEntryDTO[]; lastSeq: number };

/** Puts `later` (every entry after `from`, in order) after the kept ones. */
export function mergeEntries(
  old: readonly AgentEntryDTO[],
  from: number,
  later: readonly AgentEntryDTO[],
): AgentEntryDTO[] {
  return [...old.filter((e) => e.seq <= from), ...later];
}

/** Where the next read of a chat's entries starts (exclusive). */
export function entriesReadFrom(old: EntriesData): number {
  const first = old.items[0]?.seq ?? 1;
  const last = old.items.at(-1)?.seq ?? 0;
  let from = last;
  for (const e of old.items)
    if (e.state === "open") from = Math.min(from, e.seq - 1);
  if (old.refreshFrom !== null) from = Math.min(from, old.refreshFrom - 1);
  return Math.max(from, first - 1);
}

export const entriesQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: entriesKey(chatId),
    queryFn: async ({ client }): Promise<EntriesData> => {
      const old = client.getQueryData<EntriesData>(entriesKey(chatId));
      const path = `${chatPath(chatId)}/entries`;
      if (!old || old.items.length === 0) {
        const page = await api<EntriesAnswer>(`${path}?limit=${ENTRIES_PAGE}`);
        return {
          items: page.items,
          hasOlder: (page.items[0]?.seq ?? 1) > 1,
          refreshFrom: null,
        };
      }
      const from = entriesReadFrom(old);
      const later: AgentEntryDTO[] = [];
      let after = from;
      for (;;) {
        const page = await api<EntriesAnswer>(
          `${path}?after=${after}&limit=${ENTRIES_AFTER_MAX}`,
        );
        later.push(...page.items);
        const last = page.items.at(-1);
        if (!last || page.items.length < ENTRIES_AFTER_MAX) break;
        after = last.seq;
      }
      // An event may have arrived while reading: keep its mark if it is newer.
      const now = client.getQueryData<EntriesData>(entriesKey(chatId));
      const mark =
        now && now !== old && now.refreshFrom !== old.refreshFrom
          ? now.refreshFrom
          : null;
      return {
        items: mergeEntries(old.items, from, later),
        hasOlder: old.hasOlder,
        refreshFrom: mark,
        gap: old.gap ?? null,
      };
    },
  });

/**
 * Everything the chat's replies changed, each thing once, newest first (the
 * server reads the whole chat, not the pages read here).
 */
export const changesQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: changesKey(chatId),
    queryFn: () =>
      api<{ items: Change[] }>(`${chatPath(chatId)}/changes`).then(
        (r) => r.items,
      ),
  });

/** Puts the page before the first entry in front (scrolling up). */
export async function fetchOlderEntries(
  api: Api,
  qc: QueryClient,
  chatId: string,
): Promise<void> {
  const old = qc.getQueryData<EntriesData>(entriesKey(chatId));
  const first = old?.items[0];
  if (!old || !first || !old.hasOlder) return;
  const page = await api<EntriesAnswer>(
    `${chatPath(chatId)}/entries?before=${first.seq}&limit=${ENTRIES_PAGE}`,
  );
  qc.setQueryData<EntriesData>(entriesKey(chatId), (cur) => {
    if (!cur || cur.items[0]?.seq !== first.seq) return cur;
    return {
      ...cur,
      items: [...page.items, ...cur.items],
      hasOlder: (page.items[0]?.seq ?? 1) > 1,
    };
  });
}

/**
 * A page from `seq` on, without the pages between it and what is loaded (a
 * tap on the status bar goes to the head, `seq` 1; a tick of the outline far
 * up goes to its post: the old web's, "to the top" lands at once). The pages
 * between are read later from there down (`fetchGapEntries`); the pages
 * before it as usual (`fetchOlderEntries`).
 */
export async function fetchEntriesFrom(
  api: Api,
  qc: QueryClient,
  chatId: string,
  seq: number,
): Promise<void> {
  const old = qc.getQueryData<EntriesData>(entriesKey(chatId));
  const first = old?.items[0];
  if (!old || !first || !old.hasOlder || first.seq <= seq || old.gap) return;
  const page = await api<EntriesAnswer>(
    `${chatPath(chatId)}/entries?after=${seq - 1}&limit=${ENTRIES_PAGE}`,
  );
  qc.setQueryData<EntriesData>(entriesKey(chatId), (cur) => {
    if (!cur?.hasOlder || cur.gap) return cur;
    const put = putJumpPage(cur.items, page.items);
    return put ? { ...cur, ...put } : cur;
  });
}

/** The head of the chat, without the pages between (a tap on the status bar). */
export const fetchHeadEntries = (
  api: Api,
  qc: QueryClient,
  chatId: string,
): Promise<void> => fetchEntriesFrom(api, qc, chatId, 1);

/**
 * Reads the next page of a gap from its top (coming down from the head) and
 * answers how to put it in (null: no gap).
 */
export async function readGapEntries(
  api: Api,
  qc: QueryClient,
  chatId: string,
): Promise<(() => void) | null> {
  const gap = qc.getQueryData<EntriesData>(entriesKey(chatId))?.gap;
  if (!gap) return null;
  const page = await api<EntriesAnswer>(
    `${chatPath(chatId)}/entries?after=${gap.after}&limit=${ENTRIES_PAGE}`,
  );
  return () =>
    qc.setQueryData<EntriesData>(entriesKey(chatId), (cur) => {
      if (!cur?.gap || cur.gap.after !== gap.after) return cur;
      return {
        ...cur,
        ...putGapPage(cur.items, gap, page.items, ENTRIES_PAGE),
      };
    });
}

/** Reads the next page of a gap and puts it in at once. */
export async function fetchGapEntries(
  api: Api,
  qc: QueryClient,
  chatId: string,
): Promise<void> {
  (await readGapEntries(api, qc, chatId))?.();
}

/** A turn item as the screen gets it: with the chats around it and its Case. */
export interface TurnItem extends AgentTurnItemDTO {
  heldBy: AgentChatRef | null;
  from: AgentChatRef;
  answeredBy: AgentChatRef | null;
  case: { id: string; title: string } | null;
  /** The case the pending proposal would tie it to. */
  proposalCase: { id: string; title: string } | null;
}

export interface TurnList {
  /** Open items answered here, the first is shown. */
  open: TurnItem[];
  /** Recently closed items that leave a line in the chat, oldest first. */
  closed: TurnItem[];
  /** Items of this chat's children waiting for this chat's agent. */
  awaiting: TurnItem[];
}

export const turnQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: turnKey(chatId),
    queryFn: () => api<TurnList>(`${chatPath(chatId)}/turn`),
  });

export const runsQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: runsKey(chatId),
    queryFn: () =>
      api<{ items: AgentRunDTO[] }>(`${chatPath(chatId)}/runs`).then(
        (r) => r.items,
      ),
  });

export const pendingRequestsQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: requestsKey(chatId),
    queryFn: () =>
      api<{ items: AgentRequestDTO[] }>(
        `${chatPath(chatId)}/requests?pending=1`,
      ).then((r) => r.items),
  });

export interface ChildChat {
  id: string;
  key: string;
  title: string | null;
  status: string;
  /** When the working or asking began (null otherwise). */
  since?: string | null;
  [k: string]: unknown;
}

export const childrenQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: childrenKey(chatId),
    queryFn: () =>
      api<{ items: ChildChat[] }>(`${chatPath(chatId)}/children`).then(
        (r) => r.items,
      ),
  });

export const sideQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: sideKey(chatId),
    queryFn: () =>
      api<{ items: AgentChatDTO[] }>(`${chatPath(chatId)}/side`).then(
        (r) => r.items,
      ),
  });

/** A side chat branching off a finished reply, made by its first post. */
export function useForkSide() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: {
      entryId: string;
      /** The main chat the reply is in (its device runs the side chat). */
      chatId?: string;
      body: string;
      fileIds?: string[];
      files?: AgentEntryDTO["files"];
    }) => {
      // The side chat's id is chosen here so its first words can be signed for it.
      const sideId = globalThis.crypto.randomUUID();
      const signed =
        signer && input.chatId
          ? [await signPost(signer, qc, api, input.chatId, input, sideId)]
          : undefined;
      return api<AgentChatDTO>(
        `/api/me/agents/entries/${enc(input.entryId)}/side`,
        {
          method: "POST",
          body: { body: input.body, fileIds: input.fileIds, signed },
          idempotencyKey: idempotencyKeyFor(input),
        },
      );
    },
    // In its main chat's side chats, its outline and the lists at once (no
    // list read again).
    onSuccess: (chat) => {
      qc.setQueryData(chatKey(chat.key), chat);
      putChat(qc, chat.id, chat);
    },
  });
}

/**
 * A side chat on its main chat's outline, from the entries read (no outline
 * read again): the reply it branches off gets its tick; gone, the tick goes.
 */
function sideTickChanged(
  qc: QueryClient,
  chatId: string,
  chat: AgentChatDTO | null,
) {
  const from = chat?.forkedFromEntryId;
  if (from)
    for (const [key, data] of qc.getQueriesData<EntriesData>({
      queryKey: [...agentsPrefix, "entries"],
    })) {
      const entry = data?.items.find((e) => e.id === from);
      if (!entry) continue;
      const mainId = String(key[agentsPrefix.length + 1]);
      qc.setQueryData<AgentOutline>(outlineKey(mainId), (old) =>
        old && !old.items.some((it) => it.sideChatId === chatId)
          ? {
              ...old,
              items: [
                ...old.items.filter((it) => it.seq !== entry.seq),
                { seq: entry.seq, entryId: entry.id, sideChatId: chatId },
              ].sort((a, b) => a.seq - b.seq),
            }
          : old,
      );
    }
  if (chat) return;
  // Exact keys: the cards' texts sit under the outline's key.
  for (const [key, old] of qc.getQueriesData<AgentOutline>({
    queryKey: [...agentsPrefix, "outline"],
  }))
    if (
      key.length === agentsPrefix.length + 2 &&
      old?.items.some((it) => it.sideChatId === chatId)
    )
      qc.setQueryData<AgentOutline>(key, {
        ...old,
        items: old.items.filter((it) => it.sideChatId !== chatId),
      });
}

/**
 * The ticks of a chat's outline, read once: what changes after is taken from
 * the entries read (`outlineTicks`).
 */
export const outlineQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: outlineKey(chatId),
    queryFn: () => api<AgentOutline>(`${chatPath(chatId)}/outline`),
    staleTime: Number.POSITIVE_INFINITY,
  });

/** The text of a tick's card not among the entries read: read once. */
export const outlineHeadQuery = (api: Api, chatId: string, seq: number) =>
  queryOptions({
    queryKey: [...outlineKey(chatId), seq] as const,
    queryFn: () => api<AgentOutlineHead>(`${chatPath(chatId)}/outline/${seq}`),
    staleTime: Number.POSITIVE_INFINITY,
  });

/** What the agent's writes in a chat left to undo (one row per write). */
export interface UndoRecord {
  runId: string | null;
  kind: string;
  targetId: string;
  op: string;
  undoneAt: string | null;
  conflictAt: string | null;
}

export const undoQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: undoKey(chatId),
    queryFn: () =>
      api<{ items: UndoRecord[] }>(`${chatPath(chatId)}/undo`).then(
        (r) => r.items,
      ),
  });

/** Every request of a chat with its result (a rewind's preview is read from here). */
export const allRequestsQuery = (api: Api, chatId: string) =>
  queryOptions({
    queryKey: [...requestsKey(chatId), "all"] as const,
    queryFn: () =>
      api<{ items: AgentRequestDTO[] }>(`${chatPath(chatId)}/requests`).then(
        (r) => r.items,
      ),
  });

/** With the phones paired with a Hub (its list of who joins; under devicesKey). */
export const pairedDevicesQuery = (api: Api) =>
  queryOptions({
    queryKey: [...devicesKey, "with-phones"] as const,
    queryFn: () =>
      api<{ items: AgentDeviceDTO[] }>("/api/me/agents/devices?phones=1").then(
        (r) => r.items.map(shownDevice),
      ),
  });

export const devicesQuery = (api: Api) =>
  queryOptions({
    queryKey: devicesKey,
    queryFn: () =>
      api<{ items: AgentDeviceDTO[] }>("/api/me/agents/devices").then((r) =>
        r.items.map(shownDevice),
      ),
  });

// ---- live ------------------------------------------------------------------------

/** What arrives live about a chat's run (not stored on the server). */
export interface ChatLive {
  runId: string;
  /** The words of the segment being written; entryId null before it is stored. */
  reply: { entryId: string | null; text: string } | null;
  progress: RunLive | null;
}

/** The live state of a chat, put in by the realtime handlers only. */
export function useChatLive(chatId: string | null): ChatLive | null {
  return (
    useQuery({
      queryKey: liveKey(chatId ?? ""),
      queryFn: () => null as ChatLive | null,
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    }).data ?? null
  );
}

export function putLiveReply(
  qc: QueryClient,
  r: { chatId: string; runId: string; entryId: string | null; text: string },
) {
  qc.setQueryData<ChatLive | null>(liveKey(r.chatId), (old) => ({
    runId: r.runId,
    reply: { entryId: r.entryId, text: r.text },
    progress: old?.runId === r.runId ? old.progress : null,
  }));
}

export function putLiveProgress(
  qc: QueryClient,
  p: { chatId: string; runId: string; live: RunLive },
) {
  qc.setQueryData<ChatLive | null>(liveKey(p.chatId), (old) => ({
    runId: p.runId,
    reply: old?.runId === p.runId ? old.reply : null,
    progress: p.live,
  }));
}

// ---- realtime --------------------------------------------------------------------
//
// An event about a chat never reads the Agents list again (its pages are read
// one after another, the whole list each time) nor the other chats: the chat
// alone is read (`GET …/chats/:id`) and put where it shows, its row in every
// loaded list moved to where its last change puts it, a chat new to a list
// put in when the list's filter takes it. Child lines and side chats are read
// again only where the chat is (or comes) in them. Events of a chat arriving
// together read it once, and once more after a read already going.

const sidesPrefix = [...agentsPrefix, "side"] as const;
const childrenPrefix = [...agentsPrefix, "children"] as const;

type ChatListData = AgentChatListDTO | InfiniteData<AgentChatListDTO>;

/** What a list of the Agents list takes, read from its key. */
interface ListFilter {
  side: "open" | "active" | "archived";
  workspace: string | null;
  q: string;
}

function listFilterOf(key: QueryKey): ListFilter | null {
  const rest = key.slice(chatListsPrefix.length);
  const [side, workspace, q] = rest[0] === "pages" ? rest.slice(1) : rest;
  if (side !== "open" && side !== "active" && side !== "archived") return null;
  return {
    side,
    workspace:
      typeof workspace === "string" && workspace !== "" ? workspace : null,
    q: typeof q === "string" ? q.trim().toLowerCase() : "",
  };
}

/** Something goes on in it (the server's "active" marks, as the rows show them). */
const stirs = (c: AgentChatDTO) =>
  c.forkedFromEntryId === null &&
  (c.running !== null || c.queued || c.openItems > 0 || unread(c));

/** Whether the server would list the chat under the filter (active: see `stirs`). */
function listTakes(f: ListFilter, c: AgentChatDTO): boolean {
  if ((c.archivedAt !== null) !== (f.side === "archived")) return false;
  if (f.workspace !== null && c.workspaceId !== f.workspace) return false;
  if (f.q && !(c.title ?? "").toLowerCase().includes(f.q)) return false;
  return true;
}

/** The list's order: the newest change first (then the id, as the server's). */
const comesBefore = (a: AgentChatDTO, b: AgentChatDTO) =>
  a.updatedAt > b.updatedAt || (a.updatedAt === b.updatedAt && a.id > b.id);

/**
 * The pages with the chat as read now (null: gone): its row replaced and put
 * where its last change puts it. A chat not in the pages comes in when the
 * filter takes it and it falls within the pages read; older than them, the
 * next page brings it. The same pages when nothing changes.
 */
function placeChatInPages(
  pages: readonly AgentChatListDTO[],
  chatId: string,
  chat: AgentChatDTO | null,
  filter: ListFilter,
): readonly AgentChatListDTO[] {
  const was = pages.some((p) => p.items.some((c) => c.id === chatId));
  const keep =
    chat !== null &&
    listTakes(filter, chat) &&
    // "Active" keeps a row that calms down (the screen hides it), and takes
    // a new one only when it stirs.
    (was || filter.side !== "active" || stirs(chat));
  if (!was && !keep) return pages;
  const out = pages.map((p) => ({
    ...p,
    items: p.items.filter((c) => c.id !== chatId),
  }));
  if (!keep || !chat || out.length === 0) return out;
  for (const [i, p] of out.entries()) {
    const at = p.items.findIndex((c) => comesBefore(chat, c));
    if (at < 0) continue;
    out[i] = {
      ...p,
      items: [...p.items.slice(0, at), chat, ...p.items.slice(at)],
    };
    return out;
  }
  // Older than every row read: it stays at the end if it was there (the next
  // page's cursor is past it) or if the list has no more; else a page brings it.
  const last = out.length - 1;
  if (was || out[last].nextCursor === null)
    out[last] = { ...out[last], items: [...out[last].items, chat] };
  return out;
}

function placeInList(
  data: ChatListData,
  chatId: string,
  chat: AgentChatDTO | null,
  filter: ListFilter,
): ChatListData {
  if ("pages" in data) {
    const pages = placeChatInPages(data.pages, chatId, chat, filter);
    return pages === data.pages ? data : { ...data, pages: [...pages] };
  }
  const [page] = placeChatInPages([data], chatId, chat, filter);
  return page;
}

const listHolds = (data: ChatListData, chatId: string) =>
  ("pages" in data ? data.pages : [data]).some((p) =>
    p.items.some((c) => c.id === chatId),
  );

const isChatQuery = (chatId: string) => (q: Query) =>
  q.queryKey[chatsPrefix.length] === chatId ||
  (q.state.data as AgentChatDTO | undefined)?.id === chatId;

/** Every copy of a chat the screen holds (by itself, in a list, as a side chat). */
function cachedChats(qc: QueryClient, chatId: string): AgentChatDTO[] {
  const out: AgentChatDTO[] = [];
  for (const [, c] of qc.getQueriesData<AgentChatDTO>({
    queryKey: chatsPrefix,
  }))
    if (c?.id === chatId) out.push(c);
  for (const [, data] of qc.getQueriesData<ChatListData>({
    queryKey: chatListsPrefix,
  })) {
    const pages = !data ? [] : "pages" in data ? data.pages : [data];
    for (const p of pages)
      for (const c of p.items) if (c.id === chatId) out.push(c);
  }
  for (const [, list] of qc.getQueriesData<AgentChatDTO[]>({
    queryKey: sidesPrefix,
  }))
    for (const c of list ?? []) if (c.id === chatId) out.push(c);
  return out;
}

/**
 * Puts a chat as read now (null: gone) everywhere it shows: by itself, its
 * row in the loaded lists, among the side chats of its main chat. A side
 * chat not among any yet makes the side chats be read again.
 */
export function putChat(
  qc: QueryClient,
  chatId: string,
  answer: AgentChatDTO | null,
) {
  // The owner's read point never moves back: a read answered before a read
  // marked here (`chatRead`) keeps the newer one.
  const seen = Math.max(
    0,
    ...cachedChats(qc, chatId).map((c) => c.ownerReadSeq),
  );
  const chat =
    answer && answer.ownerReadSeq < seen
      ? { ...answer, ownerReadSeq: seen }
      : answer;
  if (chat)
    for (const q of qc
      .getQueryCache()
      .findAll({ queryKey: chatsPrefix, predicate: isChatQuery(chatId) }))
      qc.setQueryData(q.queryKey, chat);
  else
    void qc.invalidateQueries({
      queryKey: chatsPrefix,
      predicate: isChatQuery(chatId),
    });
  for (const [key, data] of qc.getQueriesData<ChatListData>({
    queryKey: chatListsPrefix,
  })) {
    const filter = listFilterOf(key);
    if (!data || !filter) continue;
    const next = placeInList(data, chatId, chat, filter);
    if (next === data) continue;
    // "Active" also lists the chats above one that stirs: a child new to it
    // whose parent is not there makes its first page be read again (rare; the
    // pages after it are let go so that one page is read).
    const parent = chat?.parentChatId;
    if (
      filter.side === "active" &&
      parent &&
      "pages" in next &&
      !listHolds(data, chatId) &&
      listHolds(next, chatId) &&
      !listHolds(next, parent)
    ) {
      qc.setQueryData(key, {
        pages: next.pages.slice(0, 1),
        pageParams: next.pageParams.slice(0, 1),
      });
      void qc.invalidateQueries({ queryKey: key, exact: true });
      continue;
    }
    qc.setQueryData(key, next);
  }
  let inSides = false;
  for (const [key, list] of qc.getQueriesData<AgentChatDTO[]>({
    queryKey: sidesPrefix,
  })) {
    if (!list?.some((c) => c.id === chatId)) continue;
    inSides = true;
    qc.setQueryData<AgentChatDTO[]>(key, (old) =>
      chat
        ? old?.map((c) => (c.id === chatId ? chat : c))
        : old?.filter((c) => c.id !== chatId),
    );
  }
  // A new side chat: its main chat is not named, and side lists are few.
  if (!inSides && chat?.forkedFromEntryId)
    void qc.invalidateQueries({ queryKey: sidesPrefix });
  // Made or deleted here or elsewhere (another window, another device).
  if (!chat || chat.forkedFromEntryId) sideTickChanged(qc, chatId, chat);
  // A family read whole for its opened rows: the chat as read now, gone
  // when deleted or moved to the other side (archived, or back).
  for (const [key, list] of qc.getQueriesData<AgentChatDTO[]>({
    queryKey: belowPrefix,
  })) {
    if (!list?.some((c) => c.id === chatId)) continue;
    const archived = key[belowPrefix.length + 1] === "archived";
    qc.setQueryData<AgentChatDTO[]>(key, (old) =>
      chat && (chat.archivedAt !== null) === archived
        ? old?.map((c) => (c.id === chatId ? chat : c))
        : old?.filter((c) => c.id !== chatId),
    );
  }
}

/** The child lines that show the chat, or would (its parents, before and after). */
function childLinesChanged(
  qc: QueryClient,
  chatId: string,
  parents: ReadonlySet<string>,
) {
  void qc.invalidateQueries({
    queryKey: childrenPrefix,
    predicate: (q) =>
      parents.has(String(q.queryKey[childrenPrefix.length])) ||
      ((q.state.data as ChildChat[] | undefined)?.some(
        (c) => c.id === chatId,
      ) ??
        false),
  });
}

const parentsOf = (chats: readonly (AgentChatDTO | null)[]) =>
  new Set(chats.flatMap((c) => (c?.parentChatId ? [c.parentChatId] : [])));

/** How long events of a chat are gathered before it is read. */
const CHAT_READ_WAIT_MS = 30;

interface ChatReading {
  fetching: boolean;
  /** An event came while reading: read once more. */
  again: boolean;
  done: Promise<void>;
}

/** The chats being read, per app (a query client), never shared. */
const readings = new WeakMap<QueryClient, Map<string, ChatReading>>();

/**
 * How many times each chat was deleted here, per app: a read started before
 * a delete answers with the chat as it was and must not put it back.
 */
const deletions = new WeakMap<QueryClient, Map<string, number>>();

function deletionsOf(qc: QueryClient) {
  let map = deletions.get(qc);
  if (!map) {
    map = new Map();
    deletions.set(qc, map);
  }
  return map;
}

async function readChatOnce(qc: QueryClient, api: Api, chatId: string) {
  const before = cachedChats(qc, chatId);
  const deleted = deletionsOf(qc).get(chatId) ?? 0;
  let chat: AgentChatDTO | null;
  try {
    chat = await api<AgentChatDTO>(chatPath(chatId));
    if ((deletionsOf(qc).get(chatId) ?? 0) !== deleted) return;
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 404)) {
      // Not read: the lists and the chat are read when next shown.
      void qc.invalidateQueries({
        queryKey: chatListsPrefix,
        refetchType: "none",
      });
      void qc.invalidateQueries({
        queryKey: chatsPrefix,
        predicate: isChatQuery(chatId),
      });
      return;
    }
    chat = null;
  }
  putChat(qc, chatId, chat);
  childLinesChanged(qc, chatId, parentsOf([...before, chat]));
}

/**
 * Reads one chat again after an event and puts it where it shows. Events of
 * the chat within CHAT_READ_WAIT_MS make one read; events during a read make
 * one more after it. Nothing is read when nothing on the screen could show it.
 */
export function refreshChat(
  qc: QueryClient,
  api: Api,
  chatId: string,
): Promise<void> {
  let map = readings.get(qc);
  if (!map) {
    map = new Map();
    readings.set(qc, map);
  }
  const going = map.get(chatId);
  if (going) {
    if (going.fetching) going.again = true;
    return going.done;
  }
  const shown =
    qc.getQueryCache().findAll({ queryKey: chatListsPrefix }).length > 0 ||
    qc.getQueryCache().findAll({ queryKey: sidesPrefix }).length > 0 ||
    qc.getQueryCache().findAll({ queryKey: childrenPrefix }).length > 0 ||
    qc
      .getQueryCache()
      .findAll({ queryKey: chatsPrefix, predicate: isChatQuery(chatId) })
      .length > 0;
  if (!shown) return Promise.resolve();
  const reading: ChatReading = {
    fetching: false,
    again: false,
    done: Promise.resolve(),
  };
  const all = map;
  all.set(chatId, reading);
  reading.done = (async () => {
    try {
      await new Promise((r) => setTimeout(r, CHAT_READ_WAIT_MS));
      do {
        reading.again = false;
        reading.fetching = true;
        await readChatOnce(qc, api, chatId);
        reading.fetching = false;
      } while (reading.again);
    } finally {
      all.delete(chatId);
    }
  })();
  return reading.done;
}

export function chatChanged(
  qc: QueryClient,
  api: Api,
  chatId: string | undefined,
  change: string | undefined,
) {
  if (!chatId) {
    // Every payload names its chat; without one, the lists are read when shown.
    void qc.invalidateQueries({
      queryKey: chatListsPrefix,
      refetchType: "none",
    });
    return;
  }
  if (change === "deleted") {
    const deleted = deletionsOf(qc);
    deleted.set(chatId, (deleted.get(chatId) ?? 0) + 1);
    for (const key of [
      entriesKey(chatId),
      changesKey(chatId),
      turnKey(chatId),
      runsKey(chatId),
      requestsKey(chatId),
      liveKey(chatId),
    ])
      qc.removeQueries({ queryKey: key });
    const parents = parentsOf(cachedChats(qc, chatId));
    putChat(qc, chatId, null);
    childLinesChanged(qc, chatId, parents);
    return;
  }
  void refreshChat(qc, api, chatId);
}

/**
 * An entry was added or changed: read again from the lowest changed seq. The
 * chat's row changes with a new entry (its last change, unread, waiting for
 * its turn), not with a reply finished; a post taken back may end its wait.
 */
export function entryChanged(
  qc: QueryClient,
  api: Api,
  chatId: string,
  seq: number,
  kind?: string,
) {
  const old = qc.getQueryData<EntriesData>(entriesKey(chatId));
  if (old) {
    const last = old.items.at(-1)?.seq ?? 0;
    if (seq <= last)
      qc.setQueryData<EntriesData>(entriesKey(chatId), {
        ...old,
        refreshFrom: Math.min(old.refreshFrom ?? seq, seq),
      });
  }
  void qc.invalidateQueries({ queryKey: entriesKey(chatId) });
  void qc.invalidateQueries({ queryKey: changesKey(chatId) });
  // A child's outcome is told in its parent: the parent's child lines.
  if (kind === "notice")
    void qc.invalidateQueries({ queryKey: childrenKey(chatId) });
  const known = cachedChats(qc, chatId);
  if (known.length === 0 || known.some((c) => seq > c.lastSeq || c.queued))
    void refreshChat(qc, api, chatId);
}

export function runChanged(qc: QueryClient, api: Api, chatId: string) {
  void qc.invalidateQueries({ queryKey: runsKey(chatId) });
  void qc.invalidateQueries({ queryKey: entriesKey(chatId) });
  void qc.invalidateQueries({ queryKey: changesKey(chatId) });
  void qc.invalidateQueries({ queryKey: undoKey(chatId) });
  void refreshChat(qc, api, chatId);
}

/** The runs already asked about by their progress, per app. */
const progressAsked = new WeakMap<QueryClient, Map<string, string>>();

/**
 * A run's progress came. A chat read before the run started does not know it
 * is running yet: read it as for the run's event, once per run (progress
 * comes many times a second, also for a chat no list shows).
 */
export function progressCame(
  qc: QueryClient,
  api: Api,
  chatId: string,
  runId: string,
) {
  if (cachedChats(qc, chatId).some((c) => c.running?.runId === runId)) return;
  let asked = progressAsked.get(qc);
  if (!asked) {
    asked = new Map();
    progressAsked.set(qc, asked);
  }
  if (asked.get(chatId) === runId) return;
  asked.set(chatId, runId);
  runChanged(qc, api, chatId);
}

export function requestChanged(qc: QueryClient, chatId: string) {
  void qc.invalidateQueries({ queryKey: requestsKey(chatId) });
  void qc.invalidateQueries({ queryKey: entriesKey(chatId) });
  void qc.invalidateQueries({ queryKey: changesKey(chatId) });
  void qc.invalidateQueries({ queryKey: runsKey(chatId) });
}

/** The owner saw a chat up to `seq` (here or in another window): never moves back. */
export function chatRead(qc: QueryClient, chatId: string, seq: number) {
  const read = (c: AgentChatDTO) =>
    c.id === chatId && c.ownerReadSeq < seq ? { ...c, ownerReadSeq: seq } : c;
  qc.setQueriesData<AgentChatDTO>({ queryKey: chatsPrefix }, (c) =>
    c ? read(c) : c,
  );
  // The Agents list (a list, or its pages): its unread marks go too.
  qc.setQueriesData<AgentChatListDTO | InfiniteData<AgentChatListDTO>>(
    { queryKey: chatListsPrefix },
    (data) => {
      if (!data) return data;
      const list = (l: AgentChatListDTO) => ({
        ...l,
        items: l.items.map(read),
      });
      return "pages" in data
        ? { ...data, pages: data.pages.map(list) }
        : list(data);
    },
  );
  // The child lines that show it.
  childLinesChanged(qc, chatId, new Set());
  // A list asked before the read lands after it and would put the old read
  // point back (the dot stays until a reload): ask it again instead.
  if (qc.isFetching({ queryKey: chatListsPrefix }) > 0)
    void qc.invalidateQueries({ queryKey: chatListsPrefix });
}

/** A turn item changed: the turn lists, and the chats' rows (their questions). */
export function turnChanged(
  qc: QueryClient,
  api: Api,
  chatIds: readonly string[],
) {
  for (const id of new Set(chatIds)) {
    void qc.invalidateQueries({ queryKey: turnKey(id) });
    void refreshChat(qc, api, id);
  }
}

// ---- writes ----------------------------------------------------------------------

export interface ChatConditions {
  deviceId?: string | null;
  agentKind?: string | null;
  model?: string | null;
  effort?: string | null;
  cwd?: string | null;
  otherMcp?: "none" | "read" | "all";
}

export interface CreateChatInput extends ChatConditions {
  /** The first post (none: an empty chat, e.g. a Case's chat made from its header). */
  body?: string;
  fileIds?: string[];
  /** The Workspace (its id) the chat belongs to. */
  workspace?: string | null;
  /** Makes it the Case's consulting chat. */
  caseId?: string;
}

export function useCreateChat() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: CreateChatInput) =>
      api<AgentChatDTO>("/api/me/agents/chats", {
        method: "POST",
        body: {
          ...input,
          signed: signer ? await signNewChat(signer, api, input) : undefined,
        },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSuccess: (chat) => {
      qc.setQueryData(chatKey(chat.key), chat);
      putChat(qc, chat.id, chat);
    },
  });
}

/** The hashes of files this screen put up (a file without one cannot be signed). */
function hashesOf(fileIds: readonly string[]): string[] {
  return fileIds.map((id) => {
    const hash = fileHashOf(id);
    if (!hash) throw new SignRefused("file_hash");
    return hash;
  });
}

/** A post's words signed for the chat (`chatId` names the chat whose device runs it; `forChat` another id, a side chat about to be made). */
async function signPost(
  signer: InstructionSigner,
  qc: QueryClient,
  api: Api,
  chatId: string,
  input: { body: string; fileIds?: string[] },
  forChat?: string,
): Promise<Signed> {
  const chat = await chatForSigning(qc, api, chatId);
  const files = hashesOf(input.fileIds ?? []);
  const signed = await signOne(signer, {
    device: chat.deviceId,
    chat: forChat ?? chat.id,
    kind: "message",
    content: { text: input.body, files, context: null },
  });
  if (!signed) throw new SignRefused("empty");
  return signed;
}

/** A new chat's conditions and first words, signed for the id chosen here. */
async function signNewChat(
  signer: InstructionSigner,
  api: Api,
  input: CreateChatInput,
): Promise<Signed[]> {
  const deviceId =
    input.deviceId ??
    (
      await api<{ items: AgentDeviceDTO[] }>("/api/me/agents/devices")
    ).items.find((d) => d.kind === "this_pc")?.id ??
    null;
  if (!deviceId) throw new SignRefused("no_device");
  const id = globalThis.crypto.randomUUID();
  const out: Signed[] = [];
  const conditions = conditionsOf(
    {
      deviceId,
      run: {
        agentKind: null,
        model: null,
        effort: null,
        cwd: null,
        otherMcp: "none",
        revision: 0,
      },
    },
    {
      agentKind: input.agentKind ?? null,
      model: input.model ?? null,
      effort: input.effort ?? null,
      cwd: input.cwd ?? null,
      otherMcp: input.otherMcp ?? "none",
    },
  );
  if (conditions) {
    const c = await signOne(signer, {
      device: deviceId,
      chat: id,
      kind: "conditions",
      content: conditions,
    });
    if (c) out.push(c);
  }
  if (input.body) {
    const files = hashesOf(input.fileIds ?? []);
    const m = await signOne(signer, {
      device: deviceId,
      chat: id,
      kind: "message",
      content: { text: input.body, files, context: null },
    });
    if (m) out.push(m);
  }
  // Even with nothing to sign, the chat's id goes with an instruction: none.
  return out;
}

/** What the person did with a turn item, signed for the chat that asked. */
async function signTurn(
  signer: InstructionSigner,
  qc: QueryClient,
  api: Api,
  item: TurnItem,
  content: {
    answer: Record<string, unknown> | null;
    files: string[];
    action: "answer" | "decline" | "slack_draft";
    reason: string | null;
  },
): Promise<Signed | undefined> {
  const chat = await chatForSigning(qc, api, item.chatId);
  return signOne(signer, {
    device: chat.deviceId,
    chat: chat.id,
    kind: "answer",
    content: { item: item.id, ...content },
  });
}

export interface PostInput {
  chatId: string;
  body: string;
  fileIds?: string[];
  /** Files as shown while the post is on its way. */
  files?: AgentEntryDTO["files"];
}

const postKey = [...agentsPrefix, "post"] as const;

/**
 * A post. It is not written into the entries' cache while on its way: a read
 * of the entries already in flight (a run streaming its replies refetches
 * often) would answer without it and the post would drop out until the server
 * answered. `usePendingPosts` shows it after the stored entries instead.
 */
export function usePost() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationKey: postKey,
    mutationFn: async (input: PostInput) => {
      const signed = signer
        ? await signPost(signer, qc, api, input.chatId, input)
        : undefined;
      return api<AgentEntryDTO>(`${chatPath(input.chatId)}/entries`, {
        method: "POST",
        body: { body: input.body, fileIds: input.fileIds, signed },
        idempotencyKey: idempotencyKeyFor(input),
      });
    },
    onSuccess: (entry, input) => {
      qc.setQueryData<EntriesData>(entriesKey(input.chatId), (cur) => {
        if (!cur) return cur;
        const items = cur.items.filter((e) => e.seq !== entry.seq);
        // Only when it closes the line: an entry after a gap waits for the
        // read (the cache stays one run of seqs; `usePendingPosts` shows the
        // answered entry meanwhile).
        if ((items.at(-1)?.seq ?? 0) === entry.seq - 1) items.push(entry);
        return { ...cur, items };
      });
    },
    // Not awaited: the post is done when the server answered, not when the
    // line is read again (the row on its way would stay beside the stored one).
    onSettled: (_d, _e, input) => {
      void qc.invalidateQueries({ queryKey: entriesKey(input.chatId) });
    },
  });
}

/**
 * The posts of a chat not in its stored entries yet, oldest first: on their
 * way (an id of their own, a seq just after the newest shown), or answered but
 * not read into the line yet (the stored entry as the server answered it, so
 * the row says it is sent at once). Shown in the render the post was made in.
 * Each keeps its object while it shows the same, so its row is not drawn again.
 */
export function usePendingPosts(
  chatId: string,
  stored: readonly AgentEntryDTO[] | undefined,
): AgentEntryDTO[] {
  const states = usePostStates();
  const last = stored?.at(-1)?.seq ?? 0;
  const shown = useRef<AgentEntryDTO[]>([]);
  return useMemo(() => {
    const next = replaceEqualDeep(
      shown.current,
      pendingEntries(chatId, states, last),
    );
    shown.current = next;
    return next;
  }, [chatId, states, last]);
}

interface PostState {
  id: number;
  status: string;
  input: PostInput | undefined;
  entry: AgentEntryDTO | undefined;
  at: number;
}

const readPostStates = (cache: MutationCache): PostState[] =>
  cache.findAll({ mutationKey: [...postKey] }).map((m) => ({
    id: m.mutationId,
    status: m.state.status,
    input: m.state.variables as PostInput | undefined,
    entry: m.state.data as AgentEntryDTO | undefined,
    at: m.state.submittedAt,
  }));

/**
 * The posts' mutations, told at once when one changes. `useMutationState`
 * tells its component a tick later (`notifyManager.schedule`): the post box
 * empties in the send's own render, and the line, which is not drawn with
 * the box, would show the post only after it.
 */
function usePostStates(): readonly PostState[] {
  const cache = useQueryClient().getMutationCache();
  const result = useRef<readonly PostState[] | null>(null);
  if (result.current === null) result.current = readPostStates(cache);
  const subscribe = useCallback(
    (changed: () => void) => {
      const read = () => {
        const next = replaceEqualDeep(result.current, readPostStates(cache));
        if (next === result.current) return false;
        result.current = next;
        return true;
      };
      const stop = cache.subscribe(() => {
        if (read()) changed();
      });
      // A change between the render and here: React reads the snapshot
      // again after subscribing.
      read();
      return stop;
    },
    [cache],
  );
  const snapshot = useCallback(
    () => result.current as readonly PostState[],
    [],
  );
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

function pendingEntries(
  chatId: string,
  states: readonly PostState[],
  last: number,
): AgentEntryDTO[] {
  const out: AgentEntryDTO[] = [];
  // The newest seq shown so far: a post on its way goes after it.
  let top = last;
  for (const s of states) {
    if (s.input?.chatId !== chatId) continue;
    if (s.status === "success") {
      // Answered: the stored entry itself (sent, not "sending"), until the
      // line has read up to it (it is there, or gone). The line's read then
      // puts the entries between in front of it; the row keeps its key (the
      // entry's id), so it stays the same row and is never shown twice.
      if (!s.entry || s.entry.seq <= last) continue;
      out.push(s.entry);
      top = Math.max(top, s.entry.seq);
      continue;
    }
    if (s.status !== "pending") continue;
    out.push({
      id: `${PENDING_POST}${s.id}`,
      chatId,
      seq: top + 0.5,
      kind: "message",
      author: "user",
      body: s.input.body,
      state: "done",
      wakes: true,
      runId: null,
      fromChatId: null,
      data: null,
      changes: null,
      files: s.input.files ?? [],
      createdAt: new Date(s.at).toISOString(),
      editedAt: null,
    });
  }
  return out;
}

export interface ChatPatch extends ChatConditions {
  title?: string | null;
  label?: string | null;
  archived?: boolean;
}

export function useUpdateChat() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: { chat: AgentChatDTO; patch: ChatPatch }) => {
      const { patch, chat } = input;
      const choosing = (
        ["deviceId", "agentKind", "model", "effort", "cwd", "otherMcp"] as const
      ).some((k) => patch[k] !== undefined);
      // Changing the conditions: the whole of them, signed for the chat.
      const conditions = choosing ? conditionsOf(chat, patch) : null;
      const signed =
        signer && conditions
          ? await signOne(signer, {
              device: conditions.device,
              chat: chat.id,
              kind: "conditions",
              content: conditions,
            })
          : undefined;
      return api<AgentChatDTO>(chatPath(chat.id), {
        method: "PATCH",
        body: { ...patch, ...(signed ? { signed } : {}) },
      });
    },
    onMutate: async ({ chat, patch }) => {
      const key = chatKey(chat.key);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<AgentChatDTO>(key);
      if (before)
        qc.setQueryData<AgentChatDTO>(key, {
          ...before,
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.deviceId !== undefined ? { deviceId: patch.deviceId } : {}),
          run: {
            ...before.run,
            ...(patch.agentKind !== undefined
              ? { agentKind: patch.agentKind }
              : {}),
            ...(patch.model !== undefined ? { model: patch.model } : {}),
            ...(patch.effort !== undefined ? { effort: patch.effort } : {}),
            ...(patch.cwd !== undefined ? { cwd: patch.cwd } : {}),
            ...(patch.otherMcp !== undefined
              ? { otherMcp: patch.otherMcp }
              : {}),
          },
        });
      return { before };
    },
    onError: (_e, { chat }, ctx) => {
      if (ctx?.before) qc.setQueryData(chatKey(chat.key), ctx.before);
    },
    onSuccess: (next) => {
      qc.setQueryData(chatKey(next.key), next);
      putChat(qc, next.id, next);
    },
  });
}

/** The owner saw the chat up to `seq` (POST …/read; the server never moves it back). */
export function useMarkChatRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { chatId: string; seq: number }) =>
      api(`${chatPath(input.chatId)}/read`, {
        method: "POST",
        body: { seq: input.seq },
      }),
    // Read up to its last entry: its Inbox row goes at once.
    onMutate: ({ chatId }) => inboxRowsSettled(qc, [`reply:${chatId}`]),
    onError: () => badgeChanged(qc),
    onSuccess: (_d, input) => chatRead(qc, input.chatId, input.seq),
  });
}

export function useDeleteChat() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chat: AgentChatDTO) =>
      api(chatPath(chat.id), { method: "DELETE" }),
    onSuccess: (_d, chat) => chatChanged(qc, api, chat.id, "deleted"),
  });
}

export interface RequestInput {
  chatId: string;
  kind: AgentRequestKind;
  entryId?: string;
  conversation?: boolean;
  files?: boolean;
  trameria?: boolean;
}

export interface RequestAnswer {
  request: AgentRequestDTO;
  /** Posts taken back before the agent read them: their text for the post box. */
  restoreText?: string | null;
  /** Their files, in order (absent when none). */
  restoreFiles?: AgentEntryFile[];
  undo?: AgentUndoResultDTO;
}

/** Stop, read now, take back, compact, rewind. */
export function useChatRequest() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async ({ chatId, ...body }: RequestInput) => {
      // Stopping goes unsigned too (it only stops); the rest is signed.
      const signed =
        signer && body.kind !== "cancel"
          ? await signOne(signer, {
              device: (await chatForSigning(qc, api, chatId)).deviceId,
              chat: (await chatForSigning(qc, api, chatId)).id,
              kind: "request",
              content: {
                kind: body.kind,
                entryId: body.entryId ?? null,
                conversation: body.conversation ?? null,
                files: body.files ?? null,
              },
            })
          : undefined;
      return api<RequestAnswer>(`${chatPath(chatId)}/requests`, {
        method: "POST",
        body: { ...body, ...(signed ? { signed } : {}) },
      });
    },
    onSuccess: (answer, input) => {
      qc.setQueryData<AgentRequestDTO[]>(requestsKey(input.chatId), (old) =>
        answer.request.status === "pending"
          ? [
              ...(old ?? []).filter((r) => r.id !== answer.request.id),
              answer.request,
            ]
          : old,
      );
    },
    onSettled: (_d, _e, input) => {
      requestChanged(qc, input.chatId);
      // A refusal can mean the screen is behind (a post already read).
      void refreshChat(qc, api, input.chatId);
    },
  });
}

export interface AnswerInput {
  item: TurnItem;
  answer: TurnAnswer;
  override?: boolean;
}

/**
 * Changes the turn list of `item`'s holder at once and puts it back when the
 * server refuses (the item's drafts stay on the device until the server
 * closes it, so nothing written is lost).
 */
function optimisticTurn<T extends { item: TurnItem }>(
  qc: QueryClient,
  apply: (list: TurnList, input: T) => TurnList,
) {
  return {
    onMutate: async (input: T) => {
      const key = turnKey(input.item.holderChatId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<TurnList>(key);
      if (previous) qc.setQueryData<TurnList>(key, apply(previous, input));
      return { previous };
    },
    onError: (
      _e: Error,
      input: T,
      ctx: { previous: TurnList | undefined } | undefined,
    ) => {
      if (ctx?.previous)
        qc.setQueryData(turnKey(input.item.holderChatId), ctx.previous);
    },
  };
}

/**
 * Answers an item: it leaves the panel at once and the next one shows. A
 * refusal puts it back; `onRefused` says why (the panel that asked is gone by
 * then, so it cannot).
 */
export function useAnswerTurn(
  options: { onRefused?(error: Error): void } = {},
) {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  const turn = optimisticTurn<AnswerInput>(qc, (list, { item }) => ({
    ...list,
    open: list.open.filter((x) => x.id !== item.id),
  }));
  return useMutation({
    mutationFn: async ({ item, answer, override }: AnswerInput) => {
      const signed = signer
        ? await signTurn(signer, qc, api, item, {
            answer: answer as Record<string, unknown>,
            files: hashesOf(answer.fileIds ?? []),
            action: "answer",
            reason: null,
          })
        : undefined;
      return api<TurnItem>(`/api/me/agents/turn/${enc(item.id)}/answer`, {
        method: "POST",
        body: {
          answer,
          version: item.version,
          ...(override ? { override } : {}),
          ...(signed ? { signed } : {}),
        },
      });
    },
    onMutate: async (input) => {
      await inboxRowsSettled(qc, [`turn:${input.item.id}`]);
      return turn.onMutate(input);
    },
    onError: (error, input, ctx) => {
      turn.onError(error, input, ctx);
      badgeChanged(qc);
      options.onRefused?.(error);
    },
    onSettled: (_d, _e, { item }) =>
      turnChanged(qc, api, [item.holderChatId, item.chatId]),
  });
}

/** Marks an item seen (at once). */
export function useTurnSeen() {
  const api = useApi();
  const qc = useQueryClient();
  const turn = optimisticTurn<{ item: TurnItem }>(qc, (list, { item }) => ({
    ...list,
    open: list.open.map((x) =>
      x.id === item.id && !x.seenAt
        ? { ...x, seenAt: new Date().toISOString() }
        : x,
    ),
  }));
  return useMutation({
    mutationFn: (item: TurnItem) =>
      api<TurnItem>(`/api/me/agents/turn/${enc(item.id)}/seen`, {
        method: "POST",
      }),
    onMutate: (item: TurnItem) => turn.onMutate({ item }),
    onError: (error, item, ctx) => turn.onError(error, { item }, ctx),
    onSettled: (_d, _e, item) => turnChanged(qc, api, [item.holderChatId]),
  });
}

export function useTurnProposal() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: { item: TurnItem; accept: boolean }) => {
      // Declining wakes the agent: signed. Accepting only changes the item.
      const signed =
        signer && !input.accept
          ? await signTurn(signer, qc, api, input.item, {
              answer: null,
              files: [],
              action: "decline",
              reason: null,
            })
          : undefined;
      return api<TurnItem>(
        `/api/me/agents/turn/${enc(input.item.id)}/proposal`,
        {
          method: "POST",
          body: {
            accept: input.accept,
            proposedAt: input.item.proposal?.proposedAt,
            ...(signed ? { signed } : {}),
          },
        },
      );
    },
    // Accepting swaps the Case badge at once (the old one stayed until the
    // list was read again).
    onMutate: async ({ item, accept }) => {
      if (!accept || !item.proposal) return;
      const key = turnKey(item.holderChatId);
      await qc.cancelQueries({ queryKey: key });
      const swap = (x: TurnItem) =>
        x.id === item.id ? { ...x, case: item.proposalCase } : x;
      qc.setQueryData<TurnList>(key, (old) =>
        old ? { ...old, open: old.open.map(swap) } : old,
      );
    },
    onSettled: (_d, _e, { item }) => turnChanged(qc, api, [item.holderChatId]),
  });
}

export function useTurnDraftEdit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      item: TurnItem;
      which: "text" | "source";
      text: string;
      subject?: string;
    }) =>
      api<TurnItem>(`/api/me/agents/turn/${enc(input.item.id)}/draft`, {
        method: "POST",
        body: {
          which: input.which,
          text: input.text,
          ...(input.subject !== undefined ? { subject: input.subject } : {}),
          version: input.item.version,
        },
      }),
    onSuccess: (next) => {
      qc.setQueryData<TurnList>(turnKey(next.holderChatId), (old) =>
        old
          ? { ...old, open: old.open.map((x) => (x.id === next.id ? next : x)) }
          : old,
      );
    },
    onSettled: (_d, _e, { item }) => turnChanged(qc, api, [item.holderChatId]),
  });
}

export function useSlackDraft() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (item: TurnItem) => {
      const signed = signer
        ? await signTurn(signer, qc, api, item, {
            answer: null,
            files: [],
            action: "slack_draft",
            reason: null,
          })
        : undefined;
      return api<TurnItem>(`/api/me/agents/turn/${enc(item.id)}/slack-draft`, {
        method: "POST",
        ...(signed ? { body: { signed } } : {}),
      });
    },
    onSettled: (_d, _e, item) => turnChanged(qc, api, [item.holderChatId]),
  });
}

export function useUpdateDevice() {
  const api = useApi();
  const qc = useQueryClient();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: {
      device: AgentDeviceDTO;
      name?: string;
      settings?: Record<string, unknown>;
    }) => {
      // The settings whole, signed for the device.
      const signed =
        signer && input.settings
          ? await signOne(signer, {
              device: input.device.id,
              chat: null,
              kind: "device_settings",
              content: {
                ...DEVICE_SETTINGS_DEFAULT,
                ...(input.device.settings as Record<string, unknown>),
                ...input.settings,
              },
            })
          : undefined;
      return api<AgentDeviceDTO>(
        `/api/me/agents/devices/${enc(input.device.id)}`,
        {
          method: "PATCH",
          body: {
            ...(input.name !== undefined ? { name: input.name } : {}),
            ...(input.settings
              ? {
                  settings: input.settings,
                  settingsRevision: input.device.settingsRevision,
                }
              : {}),
            ...(signed ? { signed } : {}),
          },
        },
      );
    },
    onSuccess: (next) =>
      qc.setQueryData<AgentDeviceDTO[]>(devicesKey, (old) =>
        old?.map((d) => (d.id === next.id ? shownDevice(next) : d)),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: devicesKey }),
  });
}

/** Removes a device (its agent is cut off; its chats keep their history). */
export function useRemoveDevice() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (device: AgentDeviceDTO) =>
      api(`/api/me/agents/devices/${enc(device.id)}`, { method: "DELETE" }),
    onSuccess: (_r, device) =>
      qc.setQueryData<AgentDeviceDTO[]>(devicesKey, (old) =>
        old?.filter((d) => d.id !== device.id),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: devicesKey }),
  });
}

/** Starts, answers (the code Claude shows) or cancels a login on a device. */
export function useDeviceLogin() {
  const api = useApi();
  const signer = useSigner();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      command:
        | { action: "start" | "cancel"; kind: string; requestId: string }
        | { action: "code"; kind: string; requestId: string; code: string };
    }) => {
      const signed = await signOne(signer, {
        device: input.deviceId,
        chat: null,
        kind: "login",
        content: input.command,
      });
      return api(`/api/me/agents/devices/${enc(input.deviceId)}/login`, {
        method: "POST",
        body: { ...input.command, ...(signed ? { signed } : {}) },
      });
    },
  });
}

export function useUndo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      chatId: string;
      entryId: string;
      target?: { kind: string; id: string };
      range?: boolean;
    }) =>
      api<AgentUndoResultDTO>(
        `${chatPath(input.chatId)}/${input.range ? "undo-range" : "undo"}`,
        {
          method: "POST",
          body: input.range
            ? { fromEntryId: input.entryId }
            : { entryId: input.entryId, target: input.target },
        },
      ),
    onSettled: (_d, _e, input) =>
      qc.invalidateQueries({ queryKey: undoKey(input.chatId) }),
  });
}

// ---- a chat's Case -----------------------------------------------------------------

export const caseStepKey = (caseId: string, stepId: string) =>
  [...agentsPrefix, "case-step", caseId, stepId] as const;
export const caseStepsPrefix = [...agentsPrefix, "case-step"] as const;

/**
 * The title of the Action a chat works on, read from its Case through the
 * Cases REST (this part does not import the Cases part). Null: gone.
 */
export const caseStepQuery = (api: Api, caseId: string, stepId: string) =>
  queryOptions({
    queryKey: caseStepKey(caseId, stepId),
    queryFn: async () => {
      const c = await api<CaseDetail>(`/api/me/cases/${enc(caseId)}`);
      const step = c.steps.find((s) => s.id === stepId);
      return step ? { id: step.id, title: step.title } : null;
    },
  });

/**
 * The Case's consulting chat, made (empty) when it has none yet. The Case's
 * own note of its chat may be stale (made from another window), so the list
 * of the Case's chats is asked before making one, and again when making it
 * is refused because one appeared meanwhile.
 */
export function useOpenCaseChat() {
  const api = useApi();
  const qc = useQueryClient();
  const find = async (caseId: string) => {
    const list = await api<AgentChatListDTO>(
      `/api/me/agents/chats?case=${enc(caseId)}&limit=50`,
    );
    return (
      list.items.find(
        (c) => c.caseId === caseId && c.caseStepId === null && !c.parentChatId,
      ) ?? null
    );
  };
  return useMutation({
    mutationFn: async (caseId: string): Promise<AgentChatDTO> => {
      const found = await find(caseId);
      if (found) return found;
      const input = { caseId };
      try {
        return await api<AgentChatDTO>("/api/me/agents/chats", {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        });
      } catch (err) {
        const again =
          err instanceof ApiError && err.code === "case_chat_exists"
            ? await find(caseId)
            : null;
        if (again) return again;
        throw err;
      }
    },
    onSuccess: (chat) => {
      qc.setQueryData(chatKey(chat.key), chat);
      putChat(qc, chat.id, chat);
    },
  });
}
